import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import frLocale from "date-fns/locale/fr";
import { exportOrderInvoiceData } from "../../api/exports";
import { format, addDays } from "date-fns";
// Import Font Awesome components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

function ExportModal() {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState("");

  const handleOpen = () => {
    setOpen(true);
    // Réinitialiser les valeurs lors de l'ouverture
    setStartDate(null);
    setEndDate(null);
    setError("");
    setIsExporting(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleExport = async () => {
    // Validation des dates
    if (!startDate || !endDate) {
      setError("Veuillez sélectionner une date de début et une date de fin");
      return;
    }

    if (startDate > endDate) {
      setError("La date de début doit être antérieure à la date de fin");
      return;
    }

    setError("");
    setIsExporting(true);

    try {
      // Formatter les dates pour l'API
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");

      // Appel à l'API pour exporter les données
      await exportOrderInvoiceData(formattedStartDate, formattedEndDate);
      
      // Fermer la modale après le succès de l'exportation
      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'exportation:", error);
      setError("Une erreur est survenue lors de l'exportation. Veuillez réessayer.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      // Utiliser timestamp 0 (1er janvier 1970) comme date de début
      const startDate = new Date(0);
      // Utiliser demain comme date de fin
      const endDate = addDays(new Date(), 1);

      // Formatter les dates pour l'API
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");

      // Appel à l'API pour exporter les données
      await exportOrderInvoiceData(formattedStartDate, formattedEndDate);
      
      // Fermer la modale après le succès de l'exportation
      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'exportation:", error);
      setError("Une erreur est survenue lors de l'exportation. Veuillez réessayer.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Excel button with FontAwesome icon */}
      <IconButton
        onClick={handleOpen}
        sx={{
          backgroundColor: "rgba(25, 210, 25, 0.12)",
          color: "rgb(51, 167, 51)",
          borderRadius: "8px",
          padding: "8px",
          "&:hover": {
            backgroundColor: "rgba(25, 210, 25, 0.2)",
          },
          transition: "all 0.2s ease-in-out",
          boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
        }}
      >
        <FontAwesomeIcon 
          icon={faFileExcel} 
          size="lg"
          style={{ 
            fontSize: "30px",
          }} 
        />
      </IconButton>

      {/* Modale d'exportation */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MDTypography variant="h5">Exportation des situations de facturation</MDTypography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            <MDTypography variant="body2" mb={2}>
              Sélectionnez une période pour exporter les situations de facturation des appareils.
            </MDTypography>
            
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Date de début"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Date de fin"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
            
            {error && (
              <MDTypography color="error" variant="caption" mt={1}>
                {error}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDBox p={1.5}>
            <MDButton color="dark" onClick={handleClose} disabled={isExporting}>
              Annuler
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleExport}
              disabled={isExporting}
              startIcon={
                isExporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon>file_download</Icon>
                )
              }
              sx={{ ml: 1 }}
            >
              {isExporting ? "Exportation en cours..." : "Exporter la période"}
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleExportAll}
              disabled={isExporting}
              startIcon={
                isExporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon>download</Icon>
                )
              }
              sx={{ ml: 1 }}
            >
              {isExporting ? "Exportation en cours..." : "Tout exporter"}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ExportModal;